'use client'

import { useState, FormEvent, ChangeEvent, useEffect } from 'react'
import { FaCheck, FaTimesCircle, FaTimes } from 'react-icons/fa'

interface FormData {
  anrede: string
  vorname: string
  nachname: string
  strasse: string
  plz: string
  ort: string
  telefon: string
  email: string
  marke: string
  modell: string
  fahrzeugstyp: string
  baujahr: string
  getriebe: string
  unfallschaden: string
  kraftstoffart: string
  klimaanlage: string
  kilometerstand: string
  preisvorstellung: string
}

export default function SubmitForm() {
  const [formData, setFormData] = useState<FormData>({
    anrede: 'bitte auswählen',
    vorname: '',
    nachname: '',
    strasse: '',
    plz: '',
    ort: '',
    telefon: '',
    email: '',
    marke: '',
    modell: '',
    fahrzeugstyp: '',
    baujahr: '',
    getriebe: 'bitte auswählen',
    unfallschaden: 'bitte auswählen',
    kraftstoffart: 'bitte auswählen',
    klimaanlage: 'bitte auswählen',
    kilometerstand: '',
    preisvorstellung: '',
  })

  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showDonePopup, setShowDonePopup] = useState(false)
  const [donePopupVisible, setDonePopupVisible] = useState(false)

  // Bot validation
  const botQuestions = [
    { question: 'Wie viele Räder hat ein Auto normalerweise?', answer: '4' },
    { question: 'Welche Farbe hat Gras? (auf Deutsch)', answer: 'grün' },
    { question: 'Wie viele Tage hat eine Woche?', answer: '7' },
    { question: 'Was ist 2 + 2?', answer: '4' },
    { question: 'Welches Tier ist bekannt für seine Streifen?', answer: 'zebra' },
  ]
  const [currentBotQuestion, setCurrentBotQuestion] = useState(botQuestions[0]) // Start with first question
  const [botAnswer, setBotAnswer] = useState('')

  // Set random question on client side to avoid hydration mismatch
  useEffect(() => {
    setCurrentBotQuestion(botQuestions[Math.floor(Math.random() * botQuestions.length)])
  }, [])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0]
    if (file) {
      const newFiles = [...files]
      newFiles[index] = file
      setFiles(newFiles)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setMessage(null)

    // Bot validation
    if (botAnswer.toLowerCase().trim() !== currentBotQuestion.answer.toLowerCase()) {
      setMessage({ type: 'error', text: 'Die Sicherheitsfrage wurde falsch beantwortet. Bitte versuchen Sie es erneut.' })
      setLoading(false)
      return
    }

    const formDataToSend = new FormData()

    // Add form fields
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value)
    })

    // Add files
    files.forEach((file, index) => {
      if (file) {
        formDataToSend.append(`file_${index}`, file)
      }
    })

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: formDataToSend,
      })

      const result = await response.json()

      if (response.ok) {
        setMessage({ type: 'success', text: 'Anfrage erfolgreich versendet! Wir werden uns in Kürze bei Ihnen melden.' })
        setFormData({
          anrede: 'bitte auswählen',
          vorname: '',
          nachname: '',
          strasse: '',
          plz: '',
          ort: '',
          telefon: '',
          email: '',
          marke: '',
          modell: '',
          fahrzeugstyp: '',
          baujahr: '',
          getriebe: 'bitte auswählen',
          unfallschaden: 'bitte auswählen',
          kraftstoffart: 'bitte auswählen',
          klimaanlage: 'bitte auswählen',
          kilometerstand: '',
          preisvorstellung: '',
        })
        setFiles([])
        setShowDonePopup(true)
        setTimeout(() => setDonePopupVisible(true), 100)
      } else {
        setMessage({ type: 'error', text: result.error || 'Es gab einen Fehler beim Senden der Anfrage.' })
      }
    } catch {
      setMessage({ type: 'error', text: 'Verbindungsfehler. Bitte versuchen Sie es später erneut.' })
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      anrede: 'bitte auswählen',
      vorname: '',
      nachname: '',
      strasse: '',
      plz: '',
      ort: '',
      telefon: '',
      email: '',
      marke: '',
      modell: '',
      fahrzeugstyp: '',
      baujahr: '',
      getriebe: 'bitte auswählen',
      unfallschaden: 'bitte auswählen',
      kraftstoffart: 'bitte auswählen',
      klimaanlage: 'bitte auswählen',
      kilometerstand: '',
      preisvorstellung: '',
    })
    setFiles([])
    setMessage(null)
    setBotAnswer('')
    setCurrentBotQuestion(botQuestions[Math.floor(Math.random() * botQuestions.length)])
  }

  // Done Popup close handler
  const closeDonePopup = () => {
    setDonePopupVisible(false)
    setTimeout(() => setShowDonePopup(false), 300)
  }

  return (
    <>
      {/* Done Popup */}
      {showDonePopup && (
        <div className={`fixed inset-0 z-50 flex items-end justify-center p-4 ${donePopupVisible ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDonePopup}></div>
          {/* Popup */}
          <div className={`relative bg-white rounded-lg shadow-xl max-w-md w-full transform transition-transform duration-300 ${donePopupVisible ? 'translate-y-0' : 'translate-y-full'}`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <FaCheck className="text-[#0066CC] text-xl" />
                <h3 className="text-lg font-semibold text-gray-900">Erfolgreich gesendet</h3>
              </div>
              <button
                onClick={closeDonePopup}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Schließen"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-700 text-base mb-4">Ihre Anfrage wurde erfolgreich versendet!<br/>Wir werden uns in Kürze bei Ihnen melden.</p>
              <button
                onClick={closeDonePopup}
                className="mt-2 px-6 py-2 bg-[#0066CC] text-white rounded-lg hover:bg-[#004BA6] transition-colors text-sm font-medium"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} id="contact-form" className="space-y-8 w-full">
      {/* Success/Error Message */}
      {message && (
        <div
          className={`p-4 md:p-6 rounded-lg flex items-start gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <FaCheck className="text-green-600 flex-shrink-0 mt-1" />
          ) : (
            <FaTimesCircle className="text-red-600 flex-shrink-0 mt-1" />
          )}
          <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>{message.text}</p>
        </div>
      )}

      {/* Kontaktdaten Section */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold mb-6 text-gray-900">Kontaktdaten</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <div>
            <label htmlFor="anrede" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Anrede</label>
            <select
              id="anrede"
              name="anrede"
              value={formData.anrede}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
            >
              <option value="bitte auswählen">bitte auswählen</option>
              <option value="Divers">Divers</option>
              <option value="Frau">Frau</option>
              <option value="Herr">Herr</option>
            </select>
          </div>

          <div>
            <label htmlFor="vorname" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Vorname</label>
            <input
              id="vorname"
              type="text"
              name="vorname"
              value={formData.vorname}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="Vorname"
            />
          </div>

          <div>
            <label htmlFor="nachname" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Nachname</label>
            <input
              id="nachname"
              type="text"
              name="nachname"
              value={formData.nachname}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="Nachname"
            />
          </div>

          <div>
            <label htmlFor="strasse" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Straße</label>
            <input
              id="strasse"
              type="text"
              name="strasse"
              value={formData.strasse}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="Straße"
            />
          </div>

          <div>
            <label htmlFor="plz" className="block text-sm md:text-base font-medium text-gray-700 mb-2">PLZ</label>
            <input
              id="plz"
              type="text"
              name="plz"
              value={formData.plz}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="PLZ"
            />
          </div>

          <div>
            <label htmlFor="ort" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Ort</label>
            <input
              id="ort"
              type="text"
              name="ort"
              value={formData.ort}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="Ort"
            />
          </div>

          <div>
            <label htmlFor="telefon" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Telefon <span className="text-red-600">*</span>
            </label>
            <input
              id="telefon"
              type="tel"
              name="telefon"
              value={formData.telefon}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="Telefon"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm md:text-base font-medium text-gray-700 mb-2">E-Mail Adresse</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="E-Mail Adresse"
            />
          </div>
        </div>
      </div>

      {/* Technische Details Section */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold mb-6 text-gray-900">Technische Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <div>
            <label htmlFor="marke" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Marke <span className="text-red-600">*</span>
            </label>
            <input
              id="marke"
              type="text"
              name="marke"
              value={formData.marke}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="z.B. VW, BMW, Mercedes"
            />
          </div>

          <div>
            <label htmlFor="modell" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Modell <span className="text-red-600">*</span>
            </label>
            <input
              id="modell"
              type="text"
              name="modell"
              value={formData.modell}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="z.B. Golf, A3, C-Klasse"
            />
          </div>

          <div>
            <label htmlFor="fahrzeugstyp" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Fahrzeugstyp</label>
            <input
              id="fahrzeugstyp"
              type="text"
              name="fahrzeugstyp"
              value={formData.fahrzeugstyp}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="z.B. Limousine, SUV, Kombi"
            />
          </div>

          <div>
            <label htmlFor="baujahr" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Baujahr <span className="text-red-600">*</span>
            </label>
            <input
              id="baujahr"
              type="text"
              name="baujahr"
              value={formData.baujahr}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="z.B. 2015"
            />
          </div>

          <div>
            <label htmlFor="getriebe" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Getriebe</label>
            <select
              id="getriebe"
              name="getriebe"
              value={formData.getriebe}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
            >
              <option value="bitte auswählen">bitte auswählen</option>
              <option value="Schaltgetriebe">Schaltgetriebe</option>
              <option value="Automatik">Automatik</option>
              <option value="Tiptronic">Tiptronic</option>
            </select>
          </div>

          <div>
            <label htmlFor="unfallschaden" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Unfallschaden</label>
            <select
              id="unfallschaden"
              name="unfallschaden"
              value={formData.unfallschaden}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
            >
              <option value="bitte auswählen">bitte auswählen</option>
              <option value="Ja">Ja</option>
              <option value="Nein">Nein</option>
              <option value="Schaden behoben">Schaden behoben</option>
            </select>
          </div>

          <div>
            <label htmlFor="kraftstoffart" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Kraftstoffart <span className="text-red-600">*</span>
            </label>
            <select
              id="kraftstoffart"
              name="kraftstoffart"
              value={formData.kraftstoffart}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
            >
              <option value="bitte auswählen">bitte auswählen</option>
              <option value="Benzin">Benzin</option>
              <option value="Diesel">Diesel</option>
              <option value="Gas">Gas</option>
              <option value="Elektro">Elektro</option>
              <option value="Sonstiges">Sonstiges</option>
            </select>
          </div>

          <div>
            <label htmlFor="klimaanlage" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Klimaanlage</label>
            <select
              id="klimaanlage"
              name="klimaanlage"
              value={formData.klimaanlage}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
            >
              <option value="bitte auswählen">bitte auswählen</option>
              <option value="vorhanden">vorhanden</option>
              <option value="nicht vorhanden">nicht vorhanden</option>
            </select>
          </div>

          <div>
            <label htmlFor="kilometerstand" className="block text-sm md:text-base font-medium text-gray-700 mb-2">Kilometerstand</label>
            <input
              id="kilometerstand"
              type="text"
              name="kilometerstand"
              value={formData.kilometerstand}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder="z.B. 150000 km"
            />
          </div>

          <div>
            <label htmlFor="preisvorstellung" className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Preisvorstellung <span className="text-red-600">*</span>
            </label>
            <input
              id="preisvorstellung"
              type="text"
              name="preisvorstellung"
              value={formData.preisvorstellung}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
              placeholder=""
            />
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">Bilder hochladen</h3>
        <p className="text-sm text-gray-600 mb-6 bg-blue-50 p-4 rounded">
          Bitte laden Sie vorzugsweise Bilder Ihres Fahrzeugs hoch. Aus technischen Gründen können nur Bilder mit den
          Endungen *.jpg, *.gif oder *.png hochgeladen werden. (Max. 3 Bilder)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map((index) => (
            <div key={index} className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input
                type="file"
                id={`file_${index}`}
                onChange={(e) => handleFileChange(e, index)}
                accept=".jpg,.jpeg,.png,.gif"
                className="hidden"
              />
              <label htmlFor={`file_${index}`} className="cursor-pointer">
                <div className="text-center">
                  <p className="text-gray-600 mb-3">Datei hochladen</p>
                  <button
                    type="button"
                    onClick={() => document.getElementById(`file_${index}`)?.click()}
                    className="bg-[#0066CC] hover:bg-[#004BA6] text-white px-4 py-2 rounded transition"
                  >
                    Durchsuchen
                  </button>
                  {files[index] && <p className="text-sm text-green-600 mt-2">{files[index].name}</p>}
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Bot Validation */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold mb-6 text-gray-900">Sicherheitsfrage</h3>
        <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
          <label htmlFor="botAnswer" className="block text-sm md:text-base font-medium text-gray-700 mb-2">{currentBotQuestion?.question}</label>
          <input
            id="botAnswer"
            type="text"
            value={botAnswer}
            onChange={(e) => setBotAnswer(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-[#0066CC] transition"
            placeholder="Ihre Antwort"
            required
          />
          <p className="text-sm text-gray-500 mt-2">Diese Frage hilft uns, Spam zu vermeiden.</p>
        </div>
      </div>

      {/* Submit Buttons */}
      <div className="flex gap-4 flex-col sm:flex-row pt-6">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-[#0066CC] hover:bg-[#004BA6] disabled:bg-gray-400 text-white px-6 py-3 md:py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
        >
          {loading ? 'Wird versendet...' : 'ANFRAGE SENDEN'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={loading}
          className="flex-1 border-2 border-gray-300 hover:border-gray-400 disabled:border-gray-200 text-gray-700 px-6 py-3 md:py-4 rounded-lg font-semibold transition-all duration-300"
        >
          Zurücksetzen
        </button>
      </div>
    </form>
    </>
  )
}
